<template>
  <div class="blog-main-content">
    <BlogList class="right"/>
    <h1>Mosaic Assassin Build Guide</h1>
    <p>The Mosaic Assassin build excels in high mobility, multi-elemental AoE damage, and survivability. With the right skills and gear, this setup is one of the most powerful Martial Arts Assassin builds in D2R.</p>

    <div>
      <img class="build-screenshot" alt="build screenshot" src="@/assets/builds/bd-mosaic-asn.png">
    </div>
    
    <div class="section">
        <h2>Core Equipment: Mosaic Runeword</h2>
        <p><strong>Runes:</strong> Mal + Gul + Amn</p>
        <p><strong>Key Attributes:</strong></p>
        <ul>
            <li>+2 to Martial Arts (Assassin Only)</li>
            <li>50% chance not to consume charge on finishing moves (100% with dual Mosaic)</li>
            <li>+8-15% Cold, Lightning, and Fire Skill Damage</li>
            <li>20% Increased Attack Speed</li>
            <li>200-250% Enhanced Damage</li>
            <li>7% Life Stolen Per Hit</li>
        </ul>
    </div>
    
    <div class="section">
        <h2>Recommended Gear</h2>
        <Equipments
          class="right"
          :left-weapon="weapon"
          :right-weapon="weapon"
          :armor="armor"
          :boots="boots"
        />
        <h3>Weapons</h3>
        <ul><li>Dual Mosaic Claws</li></ul>
        <h3>Armor</h3>
        <ul>
            <li>Enigma – +2 All Skills, teleport</li>
        </ul>
        <h3>Boots</h3>
        <ul>
          <li>Shadow Dancer – the highest kick damage of all boots, +1-2 Shadow Disciplines skills</li>
          <li>Gore Rider – Crushing blow, deadly strike, open wounds</li>
        </ul>
        <h3>Rings</h3>
        <ul>
            <li>Raven Frost – Cannot be frozen, attack rating boost</li>
        </ul>
        <h3>Amulet</h3>
        <ul><li>Mara’s Kaleidoscope – +2 Skills, resistances</li></ul>
        <h3>Other gear</h3>
        <ul><li>Any</li></ul>
    </div>

    <div id="slot-content_mpu" class="content-ad-mpu"/>

    <div class="section">
        <h2>Skill Distribution</h2>
        <div class="m-3 skills-tab right">
          <b-tabs small content-class="m-2" class="tab-text">
            <b-tab title="Martial Arts" active>
              <img class="w-100" src="@/assets/builds/skills-mosaic-asn-1.png">
            </b-tab>
            <b-tab title="Shadow Disciplines">
              <img class="w-100" src="@/assets/builds/skills-mosaic-asn-2.png">
            </b-tab>
            <b-tab title="Traps">
              <img class="w-100" src="@/assets/builds/skills-mosaic-asn-3.png">
            </b-tab>
          </b-tabs>
        </div>
        <h3>Main Charge-up Skills</h3>
        <ul>
            <li>Phoenix Strike – (Max 20 Points) Multi-elemental attack</li>
            <li>Tiger Strike – (Max 20 Points) High physical damage burst</li>
            <li>Claws of Thunder – (Max 20 Points) Lightning AoE damage</li>
            <li>Blades of Ice – (Max 20 Points) Freeze enemies for crowd control</li>
            <li>Cobra Strike – (1 Point) Life and mana leech</li>
        </ul>
        <h3>Finishing Moves (1 Point Each)</h3>
        <ul>
            <li>Dragon Talon – Primary finisher</li>
            <li>Dragon Claw</li>
            <li>Dragon Tail - Secondary finisher</li>
            <li>Dragon Flight - Teleport to monster</li>
        </ul>
        <h3>Passive & Defensive Skills</h3>
        <ul>
            <li>Claw Mastery – Add 1 point for better attack rating and damage</li>
            <li>Burst of Speed – Add 1 point for increased speed and attack speed</li>
            <li>Weapon Block – Add 1 point for better dual-claw block chance</li>
            <li>Fade – All remaining points for resistances and damage reduction</li>
        </ul>
    </div>
    
    <div class="section">
        <h2>Stat Allocation</h2>
        <ul>
            <li><strong>Strength:</strong> Just enough for gear</li>
            <li><strong>Dexterity:</strong> Just enough for gear</li>
            <li><strong>Vitality:</strong> All remaining points</li>
            <li><strong>Energy:</strong> No points needed</li>
        </ul>
    </div>
    
    <div class="section">
        <h2>Gameplay Tips</h2>
        <h4>1. Charge-up Mechanic</h4>
        <ul>
            <li>Stack Tiger Strike first for high physical damage.</li>
            <li>Charge up Claws of Thunder, Blades of Ice, and Phoenix Strike.</li>
            <li>Use Dragon Claw to unleash all charges.</li>
        </ul>
        <h4>2. Combat Rotation</h4>
        <ul>
            <li>Maintain at least one finisher every 14 seconds to refresh charges.</li>
            <li>Use Fade for resistances or Burst of Speed for mobility.</li>
        </ul>
        <h4>3. Survivability</h4>
        <ul>
            <li>Use Cobra Strike for life/mana leech (there is).</li>
            <li>Activate Fade to boost resistances and reduce physical damage.</li>
        </ul>
    </div>
  </div>
</template>

<script>
import BlogList from '../BlogList.vue'
import Equipments from './Equipments.vue'

export default {
  name: 'MosaicAsn',

  components: {
    BlogList,
    Equipments,
  },

  computed: {
    weapon () {
      return {
        'img': 'eq-mosaic-asn-weapon',
        'width': 40,
        'title': 'Mosaic Runeword',
        'runes': 'Mal + Gul + Amn',
        'detail': [
          '+2 to Martial Arts (Assassin Only)', 
          '50% chance not to consume charge on finishing moves (100% with dual Mosaic)',
          '+8-15% Cold, Lightning, and Fire Skill Damage',
          '20% Increased Attack Speed', 
          '200-250% Enhanced Damage',
          '7% Life Stolen Per Hit'
        ],
      }
    },

    armor () {
      return {
        'img': 'eq-archon-plate',
        'width': 75,
        'title': 'Enigma Runeword',
        'runes': 'Jah + Ith + Ber',
        'detail': [
          '+2 To All Skills', 
          '+45% Faster Run/Walk',
          '+1 To Teleport',
          '+750-775 Defense', 
          '0-74 To Strength (Based On Character Level)',
          'Increase Maximum Life 5%',
          'Damage Reduced By 8%',
          '+14 Life After Each Kill',
          '15% Damage Taken Goes To Mana',
          '+1-99% Better Chance of Getting Magic Items (Based On Character Level)'
        ],
      }
    },

    boots () {
      return {
        'img': 'eq-shadow-dancer',
        'width': 80,
        'title': 'Shadow Dancer',
        'detail': [
          'Defense: 122-144', 
          'Assassin kick dmg: 83-149',
          '+70-100% Enhanced Defense',
          '+1-2 To Shadow Disciplines (Assassin Only)', 
          '+30% Faster Run/Walk',
          '+30% Faster Hit Recovery',
          '+15-25 To Dexterity',
          'Requirements -20%',
        ],
      }
    },
  }
}
</script>
